import { useEffect, useMemo, useRef } from 'react';
import { onQueueCallEnded } from 'flow/actions/pbx-actions';
import { QueueKey } from 'flow/types/EntityKeys';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { makeSelectOngoingQueueCallRecords } from 'flow/selectors/queue-selectors';

interface Props {
    queueKey: QueueKey;
}

/**
 * Headless component that watches the queue's ongoing calls and brings the history
 * up to date as each one leaves the list.
 *
 * A call leaving the list has been answered and is now over. The list is what the
 * ongoing section renders, so it holds one entry per call — the hangup events behind
 * it don't, since a call rung out to several agents produces one per leg.
 *
 * Each entry carries the call record it will appear as, so the previous render's
 * entries still describe a call that has just ended, however little of it the store
 * keeps once the channel hangs up. An entry with nothing against it is a call that
 * was already under way when we connected; it is still asked for, just not by name.
 *
 * Isolated so that selector changes don't re-render the history list.
 */
const EndedQueueCallHistoryFetcher = ({ queueKey }: Props): null => {
    const dispatch = useAppDispatch();

    const selectOngoingQueueCallRecords = useMemo(
        makeSelectOngoingQueueCallRecords,
        [],
    );
    const ongoingQueueCalls = useAppSelector((state) =>
        selectOngoingQueueCallRecords(state, queueKey),
    );

    const previousOngoingQueueCalls = useRef(ongoingQueueCalls);

    useEffect(() => {
        const endedQueueCalls = previousOngoingQueueCalls.current?.filter(
            (_queueCall: unknown, channelID: string) =>
                !ongoingQueueCalls?.has(channelID),
        );

        previousOngoingQueueCalls.current = ongoingQueueCalls;

        endedQueueCalls?.forEach((endedQueueCall) =>
            // Nothing known about the call means the channel was already handling it
            // when we connected, leaving no waiting call to match it to. Asking
            // without naming it still works, since the fetch then anchors itself on
            // the calls the history holds as ongoing — this one among them.
            dispatch(
                onQueueCallEnded(
                    queueKey,
                    endedQueueCall?.get('callRecordKey'),
                    endedQueueCall?.get('queueJoinTime'),
                ),
            ),
        );
    }, [ongoingQueueCalls, queueKey, dispatch]);

    return null;
};

export default EndedQueueCallHistoryFetcher;
